import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  Loader2,
  MessageSquare,
  RefreshCw,
  Smartphone,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
// import telecelLogo from "../../assets/payment/telecel.png";
import mtnLogo from "../../assets/payment/mtn.png";
// import airtelTigoLogo from "../../assets/payment/airteltigo.png";
import { formatCurrency } from "../../data/mock/dashboardData";
import { showError, showInfo, showSuccess } from "../../utils/toast";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchOpsCase, initiateOpsPayment, normalizeOpsCase } from "../../services/institutionWorkspaceApi";
import { verifyOpsPaymentStatus } from "../../services/paymentApi";
import { useSelector } from "react-redux";

const NETWORKS = [
  // {
  //   id: "telecel",
  //   label: "Telecel Cash",
  //   logo: telecelLogo,
  //   hint: "Prompt sent to Telecel Cash wallet",
  // },
  {
    id: "mtn",
    label: "MTN MoMo",
    logo: mtnLogo,
    hint: "Prompt sent to MTN Mobile Money",
  },
  // {
  //   id: "airteltigo",
  //   label: "AirtelTigo Money",
  //   logo: airtelTigoLogo,
  //   hint: "Prompt sent to AirtelTigo Money",
  // },
];

const METHODS = [
  {
    id: "momo",
    label: "Mobile money",
    description: "Send a MoMo payment prompt to the customer’s wallet",
    icon: Smartphone,
  },
];

const FLOW_STEPS = [
  { key: "validate", label: "Validating details" },
  { key: "link", label: "Linking case balance" },
  { key: "prompt", label: "Sending payment prompt" },
  { key: "confirm", label: "Confirming initiation" },
];

const PROCESSING_MS = 450 + (FLOW_STEPS.length - 1) * 520 + 400;
const POLL_INTERVAL_MS = 3500;
const POLL_WINDOW_MS = 36000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function paymentFromPayload(payload) {
  return payload?.payment || payload?.data?.payment || null;
}

function paymentFromError(error) {
  const body = error?.body;
  return body?.data?.data?.payment || body?.data?.payment || null;
}

function normalizeStatus(payment) {
  return String(payment?.status || "").toLowerCase();
}

function paymentOutcome(status) {
  if (["completed", "success", "successful", "paid"].includes(status)) return "success";
  if (["failed", "cancelled", "canceled", "rejected"].includes(status)) return "failed";
  if (status) return "pending";
  return "";
}

export default function InitiatePaymentModal({ caseRow, onClose, onComplete }) {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [method, setMethod] = useState("momo");
  const [network, setNetwork] = useState("mtn");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [promptMessage, setPromptMessage] = useState("");
  const [phase, setPhase] = useState("form"); // form | processing | waiting | success | pending | failed
  const [flowStep, setFlowStep] = useState(0);
  const [resultAmount, setResultAmount] = useState(0);
  const [resultReference, setResultReference] = useState("");
  const [resultPaymentId, setResultPaymentId] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [waitProgress, setWaitProgress] = useState(0);
  const [checking, setChecking] = useState(false);
  const [resultBalance, setResultBalance] = useState(null);
  const openedFor = useRef("");

  const outstanding = useMemo(() => {
    if (!caseRow) return 0;
    const owed = Number(caseRow.amountOwed ?? 0);
    const paid = Number(caseRow.amountPaid ?? 0);
    const reported = Number(caseRow.outstandingAmount ?? caseRow.outstanding);
    const derived = Math.max(0, owed - paid);
    const chosen =
      Number.isFinite(reported) && reported > 0 && paid > 0 && owed > paid && reported >= owed
        ? derived
        : Number.isFinite(reported) && reported >= 0
          ? reported
          : derived;
    return Number(chosen.toFixed(2));
  }, [caseRow]);

  useEffect(() => {
    if (!caseRow?.id) {
      openedFor.current = "";
      return;
    }
    if (openedFor.current === String(caseRow.id)) return;
    openedFor.current = String(caseRow.id);
    setPhone(caseRow.phone ?? caseRow.customerPhone ?? "");
    setAmount(outstanding > 0 ? Number(outstanding).toFixed(2) : "");
    setMethod("momo");
    setNetwork("mtn");
    setPromptMessage(caseRow.customer ? `Repayment for ${caseRow.customer}` : "SimCollect repayment");
    setPhase("form");
    setFlowStep(0);
    setResultAmount(0);
    setResultReference("");
    setResultPaymentId("");
    setStatusMessage("");
    setWaitProgress(0);
    setChecking(false);
    setResultBalance(null);
  }, [caseRow, outstanding]);

  useEffect(() => {
    if (phase !== "processing") return undefined;
    const timers = FLOW_STEPS.map((_, index) =>
      setTimeout(() => setFlowStep(index + 1), 450 + index * 520)
    );
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  if (!caseRow) return null;

  const selectedNetwork = NETWORKS.find((n) => n.id === network);
  const busy = phase === "processing" || phase === "waiting" || checking;

  const loadLiveBalance = async (outcome, amountPaid) => {
    let nextBalance = outstanding;
    if (useLiveApi && caseRow?.id) {
      try {
        const raw = await fetchOpsCase(token, caseRow.id);
        const normalized = raw ? normalizeOpsCase(raw) : null;
        const reported = Number(normalized?.outstandingAmount);
        if (Number.isFinite(reported) && reported >= 0) nextBalance = reported;
      } catch {
        // Keep the balance already on this case if the refresh does not return.
      }
    }
    if (outcome === "success") {
      nextBalance = Math.min(nextBalance, Math.max(0, outstanding - amountPaid));
    }
    setResultBalance(nextBalance);
    return nextBalance;
  };

  const showOutcome = async (payment, fallbackPhase = "pending") => {
    const status = normalizeStatus(payment);
    const referenceId = payment?.reference_id || payment?.reference || resultReference;
    const paymentId = payment?.payment_id || payment?.id || resultPaymentId;
    const outcome = paymentOutcome(status) || fallbackPhase;
    const amountPaid = outcome === "success"
      ? Number(payment?.amount) || Number(amount) || resultAmount || 0
      : 0;
    if (referenceId) setResultReference(referenceId);
    if (paymentId) setResultPaymentId(paymentId);
    setResultAmount(outcome === "success" ? amountPaid : Number(payment?.amount) || Number(amount) || resultAmount || 0);
    await loadLiveBalance(outcome, outcome === "success" ? amountPaid : 0);

    if (outcome === "success") {
      setPhase("success");
      setStatusMessage("The customer approved this payment.");
      showSuccess(`Payment of ${formatCurrency(amountPaid)} received.`);
      return;
    }

    if (outcome === "failed") {
      const detail = payment?.provider_status
        ? `Gateway reported ${payment.provider_status}.`
        : "The payment did not complete.";
      setPhase("failed");
      setStatusMessage(detail);
      showError(detail);
      return;
    }

    setPhase("pending");
    setStatusMessage("The customer has not confirmed yet. The prompt is still waiting on their phone.");
    showInfo(referenceId ? `Payment is pending. Reference ${referenceId}.` : "Payment is pending.");
  };

  const pollPaymentStatus = async (paymentId, seedPayment) => {
    if (!paymentId || !useLiveApi) {
      await showOutcome(seedPayment, "pending");
      return;
    }

    const started = Date.now();
    setPhase("waiting");
    setWaitProgress(0);
    let latest = seedPayment;

    while (Date.now() - started < POLL_WINDOW_MS) {
      const elapsed = Date.now() - started;
      setWaitProgress(Math.min(100, (elapsed / POLL_WINDOW_MS) * 100));
      await sleep(POLL_INTERVAL_MS);
      try {
        const checked = await verifyOpsPaymentStatus(token, paymentId);
        latest = paymentFromPayload(checked) || latest;
        const outcome = paymentOutcome(normalizeStatus(latest));
        if (outcome === "success" || outcome === "failed") {
          setWaitProgress(100);
          await showOutcome(latest);
          return;
        }
      } catch {
        // Keep the ring running until the wait window ends.
      }
    }

    setWaitProgress(100);
    await showOutcome(latest, "pending");
  };

  const handleDone = () => {
    onComplete?.({
      caseId: caseRow.id,
      method,
      network: method === "momo" ? network : null,
      phone: phone.trim(),
      amount: resultAmount,
      balance: resultBalance,
      promptMessage: promptMessage.trim(),
      status: phase,
      referenceId: resultReference,
      paymentId: resultPaymentId,
    });
    onClose?.();
  };

  const handleCheckStatus = async () => {
    if (!resultPaymentId || !useLiveApi) return;
    setChecking(true);
    try {
      const checked = await verifyOpsPaymentStatus(token, resultPaymentId);
      await showOutcome(paymentFromPayload(checked), "pending");
    } catch (error) {
      showError(error.message || "Could not verify payment status.");
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async () => {
    const parsedAmount = Number(amount);
    if (!phone.trim()) {
      showError("Enter the mobile number that should receive the prompt or SMS.");
      return;
    }
    if (!parsedAmount || parsedAmount <= 0) {
      showError("Enter a valid payment amount.");
      return;
    }
    if (parsedAmount > outstanding && outstanding > 0) {
      showError(`Amount cannot exceed outstanding balance (${formatCurrency(outstanding)}).`);
      return;
    }

    const startedAt = Date.now();
    setPhase("processing");
    setFlowStep(0);
    setResultAmount(parsedAmount);
    setResultBalance(null);

    try {
      let payment = null;
      if (useLiveApi) {
        const initiated = await initiateOpsPayment(token, caseRow.id, {
          amount: parsedAmount,
          payment_type: method === "momo" ? "momo" : "card",
          interaction_method: method,
          phone_number: phone.trim(),
          network: method === "momo" ? network : undefined,
          prompt_message: promptMessage.trim(),
        });
        payment = paymentFromPayload(initiated);
      } else {
        await sleep(PROCESSING_MS);
        payment = { status: "pending", payment_id: "PAY-DEMO", reference_id: "demo-ref" };
      }

      const referenceId = payment?.reference_id || payment?.reference || "";
      const paymentId = payment?.payment_id || payment?.id || "";
      setResultReference(referenceId);
      setResultPaymentId(paymentId);

      const elapsed = Date.now() - startedAt;
      if (elapsed < PROCESSING_MS) await sleep(PROCESSING_MS - elapsed);
      setFlowStep(FLOW_STEPS.length);

      const immediate = paymentOutcome(normalizeStatus(payment));
      if (immediate === "failed") {
        await showOutcome(payment, "failed");
        return;
      }

      await pollPaymentStatus(paymentId, payment);
    } catch (error) {
      const payment = paymentFromError(error);
      const elapsed = Date.now() - startedAt;
      if (elapsed < PROCESSING_MS) await sleep(PROCESSING_MS - elapsed);
      setFlowStep(FLOW_STEPS.length);
      setResultPaymentId(payment?.payment_id || payment?.id || "");
      setResultReference(payment?.reference_id || payment?.reference || "");
      setStatusMessage(error.message || "Could not initiate payment.");
      setPhase("failed");
      setResultBalance(outstanding);
      showError(error.message || "Could not initiate payment.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-primary/40 p-4 backdrop-blur-sm sm:items-center"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="initiate-payment-title"
        className="flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
      >
        <div className="h-1 bg-gradient-to-r from-primary via-accent to-collect" aria-hidden />
        <div className="border-b border-primary/[0.06] px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
                Initiate payment · {caseRow.id}
              </p>
              <h2 id="initiate-payment-title" className="mt-1 text-lg font-bold text-primary">
                {caseRow.customer}
              </h2>
              <p className="mt-1 text-sm text-primary/50">
                Outstanding {formatCurrency(outstanding)}
                {caseRow.institution ? ` · ${caseRow.institution}` : ""}
              </p>
            </div>
            {phase === "form" || phase === "pending" || phase === "failed" || phase === "success" ? (
              <button
                type="button"
                onClick={phase === "form" ? onClose : handleDone}
                className="rounded-lg p-2 text-primary/40 hover:bg-primary/5"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {phase === "form" ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
                <div className="rounded-xl border border-accent/20 bg-accent/[0.06] px-3.5 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-xs font-semibold text-primary/70">
                      <Wallet className="h-3.5 w-3.5 text-accent-dark" />
                      Amount owed (editable)
                    </span>
                    <button
                      type="button"
                      className="text-[11px] font-bold text-accent-dark hover:text-primary"
                      onClick={() => setAmount(outstanding > 0 ? Number(outstanding).toFixed(2) : "")}
                    >
                      Use full outstanding
                    </button>
                  </div>
                  <Input
                    label="Agreed amount (₵)"
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={outstanding ? String(outstanding) : "0.00"}
                  />
                  <p className="mt-1.5 text-[11px] text-primary/45">
                    Prefills the customer’s outstanding balance. Lower it for a partial / agreed amount.
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs font-semibold text-primary/50">Payment method</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {METHODS.map((item) => {
                      const selected = method === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setMethod(item.id)}
                          className={`flex items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                            selected
                              ? "border-accent/40 bg-accent/[0.08] ring-1 ring-accent/20"
                              : "border-primary/[0.08] hover:border-primary/15"
                          }`}
                        >
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                              selected
                                ? "bg-white text-primary shadow-sm"
                                : "bg-primary/[0.06] text-primary/60"
                            }`}
                          >
                            <item.icon className="h-4 w-4" />
                          </span>
                          <span>
                            <span className="block text-sm font-bold text-primary">{item.label}</span>
                            <span className="mt-0.5 block text-[11px] text-primary/45">
                              {item.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {method === "momo" ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-primary/50">Mobile money network</p>
                    <div className="grid grid-cols-3 gap-2">
                      {NETWORKS.map((item) => {
                        const selected = network === item.id;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setNetwork(item.id)}
                            className={`flex flex-col items-center gap-2 rounded-xl border px-2 py-3 transition-all ${
                              selected
                                ? "border-accent/50 bg-accent/[0.08] ring-2 ring-accent/25"
                                : "border-primary/[0.08] hover:border-primary/15"
                            }`}
                          >
                            <img
                              src={item.logo}
                              alt={item.label}
                              className="h-12 w-12 rounded-lg object-cover shadow-sm ring-1 ring-black/5"
                            />
                            <span className="text-center text-[11px] font-semibold text-primary">
                              {item.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedNetwork ? (
                      <p className="mt-2 text-[11px] text-primary/45">{selectedNetwork.hint}</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex items-start gap-3 rounded-xl border border-sky-200/70 bg-sky-50/70 px-4 py-3">
                    <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                    <p className="text-xs leading-relaxed text-sky-900/80">
                      We will SMS a one-time payment link to the number below. The customer opens the
                      link, enters card details, and completes payment securely.
                    </p>
                  </div>
                )}

                <Input
                  label={method === "momo" ? "MoMo wallet number" : "Phone number for SMS link"}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 024XXXXXXX"
                  icon={Smartphone}
                />

                <div className="rounded-xl border border-sky-200/80 bg-sky-50/80 px-3.5 py-3">
                  <p className="text-xs font-bold text-sky-950">Shown on the customer's prompt</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-sky-900/75">
                    The customer sees the amount, a payment reference created when you send, and this
                    message. Keep it short.
                  </p>
                  <textarea
                    value={promptMessage}
                    onChange={(e) => setPromptMessage(e.target.value.slice(0, 160))}
                    rows={2}
                    maxLength={160}
                    placeholder="e.g. October repayment"
                    className="mt-2 w-full rounded-xl border border-sky-200 bg-white px-3 py-2.5 text-sm text-primary outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-200/70"
                  />
                  <p className="mt-1 text-[10px] font-semibold text-sky-900/50">
                    {promptMessage.trim().length}/160 · sent with the prompt
                  </p>
                </div>

              </div>

              <div className="flex justify-end gap-2 border-t border-primary/[0.06] bg-primary/[0.02] px-5 py-4 sm:px-6">
                <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSubmit} disabled={busy}>
                  {method === "momo" ? "Send MoMo prompt" : "Send card payment SMS"}
                </Button>
              </div>
            </motion.div>
          ) : null}

          {phase === "processing" ? (
            <motion.div
              key="processing"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-1 flex-col items-center justify-center px-6 py-12"
            >
              <motion.div
                className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/[0.06]"
                animate={{ rotate: 360 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
              >
                <Loader2 className="h-9 w-9 text-primary" />
              </motion.div>
              <p className="text-sm font-bold text-primary">Sending payment prompt…</p>
              <p className="mt-1 text-xs text-primary/45">
                {formatCurrency(resultAmount)} · {phone.trim()}
              </p>
              <ul className="mt-8 w-full max-w-xs space-y-3">
                {FLOW_STEPS.map((step, index) => {
                  const done = flowStep > index;
                  const active = flowStep === index;
                  return (
                    <li key={step.key} className="flex items-center gap-3 text-xs">
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full ${
                          done
                            ? "bg-emerald-500 text-white"
                            : active
                              ? "bg-accent text-primary"
                              : "bg-primary/[0.06] text-primary/35"
                        }`}
                      >
                        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : index + 1}
                      </span>
                      <span className={done || active ? "font-semibold text-primary" : "text-primary/40"}>
                        {step.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          ) : null}

          {phase === "waiting" ? (
            <motion.div
              key="waiting"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
            >
              <div className="relative mb-6 flex h-24 w-24 items-center justify-center">
                <svg className="absolute inset-0 h-24 w-24 -rotate-90" viewBox="0 0 96 96" aria-hidden>
                  <circle cx="48" cy="48" r="40" fill="none" stroke="currentColor" className="text-primary/10" strokeWidth="6" />
                  <motion.circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="none"
                    stroke="currentColor"
                    className="text-collect"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={251}
                    animate={{ strokeDashoffset: 251 - (251 * waitProgress) / 100 }}
                    transition={{ duration: 0.35 }}
                  />
                </svg>
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
              <h3 className="text-lg font-bold text-primary">Waiting for approval</h3>
              <p className="mt-2 max-w-sm text-sm text-primary/55">
                Prompt sent to {phone.trim()} for {formatCurrency(resultAmount)}. Checking status for about 35 seconds…
              </p>
              {resultReference ? (
                <p className="mt-2 text-xs font-semibold text-primary/60">Ref {resultReference}</p>
              ) : null}
              <p className="mt-4 text-[11px] font-semibold text-primary/40">{Math.round(waitProgress)}%</p>
            </motion.div>
          ) : null}

          {phase === "success" ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
            >
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 280, damping: 16 }}
                className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600"
              >
                <CheckCircle2 className="h-10 w-10" />
              </motion.div>
              <h3 className="text-lg font-bold text-primary">Payment received</h3>
              <p className="mt-2 max-w-sm text-sm text-primary/55">
                {statusMessage || `${formatCurrency(resultAmount)} from ${phone.trim()} was confirmed.`}
              </p>
              <PaymentResultFacts
                reference={resultReference}
                amountPaid={resultAmount}
                balance={resultBalance}
              />
              <Button className="mt-8" size="sm" onClick={handleDone}>
                Done
              </Button>
            </motion.div>
          ) : null}

          {phase === "pending" ? (
            <motion.div
              key="pending"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
            >
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/15 text-amber-600">
                <Clock className="h-10 w-10" />
              </div>
              <h3 className="text-lg font-bold text-primary">Payment pending</h3>
              <p className="mt-2 max-w-sm text-sm text-primary/55">
                {statusMessage ||
                  `The prompt for ${formatCurrency(resultAmount)} is still waiting on the customer.`}
              </p>
              <PaymentResultFacts
                reference={resultReference}
                amountPaid={0}
                balance={resultBalance ?? outstanding}
                requested={resultAmount}
              />
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {resultPaymentId ? (
                  <Button size="sm" variant="outline" onClick={handleCheckStatus} disabled={checking}>
                    <RefreshCw className={`h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
                    {checking ? "Checking…" : "Check payment status"}
                  </Button>
                ) : null}
                <Button size="sm" onClick={handleDone}>
                  Done
                </Button>
              </div>
            </motion.div>
          ) : null}

          {phase === "failed" ? (
            <motion.div
              key="failed"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center"
            >
              <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-rose-500/15 text-rose-600">
                <XCircle className="h-10 w-10" />
              </div>
              <h3 className="text-lg font-bold text-primary">Payment failed</h3>
              <p className="mt-2 max-w-sm text-sm text-primary/55">
                {statusMessage || "The payment could not be completed."}
              </p>
              <PaymentResultFacts
                reference={resultReference}
                amountPaid={0}
                balance={resultBalance ?? outstanding}
                requested={resultAmount}
              />
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {resultPaymentId ? (
                  <Button size="sm" variant="outline" onClick={handleCheckStatus} disabled={checking}>
                    <RefreshCw className={`h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} />
                    {checking ? "Checking…" : "Check payment status"}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPhase("form");
                    setFlowStep(0);
                    setStatusMessage("");
                  }}
                >
                  Retry
                </Button>
                <Button size="sm" onClick={handleDone}>
                  Done
                </Button>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function PaymentResultFacts({ reference, amountPaid, balance, requested }) {
  return (
    <dl className="mt-5 w-full max-w-sm space-y-2 rounded-xl bg-primary/[0.03] px-4 py-3 text-left text-sm">
      <div className="flex items-center justify-between gap-3">
        <dt className="text-primary/50">Reference</dt>
        <dd className="font-semibold text-primary">{reference || "—"}</dd>
      </div>
      {requested > 0 && requested !== amountPaid ? (
        <div className="flex items-center justify-between gap-3">
          <dt className="text-primary/50">Amount requested</dt>
          <dd className="font-semibold text-primary">{formatCurrency(requested)}</dd>
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <dt className="text-primary/50">Amount paid</dt>
        <dd className="font-semibold text-primary">{formatCurrency(amountPaid || 0)}</dd>
      </div>
      <div className="flex items-center justify-between gap-3">
        <dt className="text-primary/50">Balance</dt>
        <dd className="font-semibold text-primary">{formatCurrency(balance ?? 0)}</dd>
      </div>
    </dl>
  );
}
